'use client'

import Link from 'next/link'
import { useState } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { ChevronUp } from 'lucide-react'
import { useAppSession } from '@/state/session/context'
import { cn } from '@/lib/utils'
import {
  getDesktopNavigationGroups,
  getMobilePrimaryNavigationItems,
  isNavigationItemActive,
} from '@/lib/config/navigation'
import {
  Drawer,
  DrawerContent,
  DrawerClose,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer'

export function BottomNav(): React.JSX.Element {
  const pathname = usePathname()
  const router = useRouter()
  const { session } = useAppSession()
  const [moreOpen, setMoreOpen] = useState(false)

  const primaryItems = getMobilePrimaryNavigationItems()
  const primaryHrefs = new Set(primaryItems.map((item) => item.href))
  const navigationBadges =
    (session as { navigationBadges?: Record<string, number> }).navigationBadges ?? {}

  const navGroups = getDesktopNavigationGroups()

  // Check if any non-primary item is active
  const isMoreActive = navGroups.some((group) =>
    group.items.some(
      (item) => !primaryHrefs.has(item.href) && isNavigationItemActive(pathname, item)
    )
  )

  // Total badges for non-primary items
  const moreBadgeCount = navGroups.reduce((total, group) => {
    return (
      total +
      group.items
        .filter((item) => !primaryHrefs.has(item.href))
        .reduce((acc, item) => {
          if (item.badgeKey && navigationBadges[item.badgeKey]) {
            return acc + navigationBadges[item.badgeKey]
          }
          return acc
        }, 0)
    )
  }, 0)

  const handleMoreItemClick = (href: string) => {
    setMoreOpen(false)
    router.push(href)
  }

  return (
    <Drawer open={moreOpen} onOpenChange={setMoreOpen}>
      <nav
        className="fixed inset-x-0 bottom-0 z-50 flex items-center justify-around bg-nav-bg px-2 pb-[env(safe-area-inset-bottom,12px)] pt-1 lg:hidden"
        style={{ borderTop: 'var(--border-w) solid var(--border)' }}
        aria-label="Navegação principal"
      >
        {/* Primary nav items */}
        {primaryItems.map((item) => {
          const isActive = isNavigationItemActive(pathname, item)
          const Icon = item.icon
          const badgeValue = item.badgeKey ? navigationBadges[item.badgeKey] : 0

          return (
            <Link
              key={item.href}
              href={item.href}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                'relative flex min-h-[48px] min-w-[52px] flex-col items-center justify-center gap-1 px-1.5 py-2 text-xs font-bold transition-colors',
                isActive ? 'text-nav-active' : 'text-muted-foreground active:text-foreground'
              )}
            >
              {isActive && (
                <span
                  aria-hidden="true"
                  className="absolute inset-0 rounded-lg bg-nav-active/10"
                />
              )}
              <Icon
                className={cn('relative z-10 size-[1.325rem]', isActive && 'text-nav-active')}
                aria-hidden="true"
              />
              <span className="relative z-10">{item.label}</span>
              {badgeValue > 0 && (
                <span 
                  className="absolute -top-1 -right-1 z-20 flex size-4 pointer-events-none items-center justify-center rounded-full bg-tint-primary-subtle text-xs font-black text-primary"
                  aria-label={`${badgeValue > 9 ? 'Mais de 9' : badgeValue} notificações`}
                >
                  {badgeValue > 9 ? '9+' : badgeValue}
                </span>
              )}
            </Link>
          )
        })}

        {/* "Mais" button */}
        <DrawerTrigger asChild>
          <button
            type="button"
            aria-expanded={moreOpen}
            aria-label="Mais opções de navegação"
            className={cn(
              'relative flex min-h-[48px] min-w-[52px] flex-col items-center justify-center gap-1 px-1.5 py-2 text-xs font-bold transition-colors',
              isMoreActive || moreOpen
                ? 'text-nav-active'
                : 'text-muted-foreground active:text-foreground'
            )}
          >
            <ChevronUp
              className={cn(
                'relative z-10 size-[1.325rem]',
                (isMoreActive || moreOpen) && 'text-nav-active'
              )}
              aria-hidden="true"
            />
            <span className="relative z-10">Mais</span>
            {moreBadgeCount > 0 && (
              <span 
                className="absolute -top-1 -right-1 z-20 flex size-4 pointer-events-none items-center justify-center rounded-full bg-tint-primary-subtle text-xs font-black text-primary"
                aria-label={`${moreBadgeCount > 9 ? 'Mais de 9' : moreBadgeCount} notificações`}
              >
                {moreBadgeCount > 9 ? '9+' : moreBadgeCount}
              </span>
            )}
          </button>
        </DrawerTrigger>

        <DrawerContent>
          <DrawerHeader className="sr-only">
            <DrawerTitle>Menu de Navegação Adicional</DrawerTitle>
            <DrawerDescription>Acesso às demais seções do aplicativo</DrawerDescription>
          </DrawerHeader>
          <div className="mx-auto mt-4 h-1.5 w-12 shrink-0 rounded-full bg-muted/20" />
          <div className="flex flex-col gap-8 px-4 pb-12 pt-6">
            {navGroups.map((group) => {
              const groupItems = group.items.filter((item) => !primaryHrefs.has(item.href))
              if (groupItems.length === 0) return null

              return (
                <section key={group.id}>
                  <p className="mb-2 px-1 text-xs font-black tracking-[0.18em] text-muted-foreground uppercase">
                    {group.label}
                  </p>
                  <nav className="flex flex-col gap-1">
                    {groupItems.map((item) => {
                      const isActive = isNavigationItemActive(pathname, item)
                      const Icon = item.icon
                      const badgeValue = item.badgeKey ? navigationBadges[item.badgeKey] : 0

                      return (
                        <DrawerClose asChild key={item.href}>
                          <button
                            type="button"
                            onClick={() => handleMoreItemClick(item.href)}
                            className={cn(
                              'flex min-h-[48px] w-full items-center gap-4 rounded-xl px-4 text-base font-bold transition-[transform,background-color,color,border-color] active:scale-[0.98]',
                              isActive
                                ? 'bg-tint-primary-subtle text-primary border border-primary/20'
                                : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground border border-transparent'
                            )}
                          >
                            <Icon className="size-5 shrink-0" aria-hidden="true" />
                            <span>{item.label}</span>
                            {badgeValue > 0 && (
                              <span 
                                className="ml-auto rounded-full bg-tint-primary-subtle px-2.5 py-0.5 text-xs font-black text-primary"
                                aria-label={`${badgeValue > 9 ? 'Mais de 9' : badgeValue} notificações`}
                              >
                                {badgeValue > 9 ? '9+' : badgeValue}
                              </span>
                            )}
                          </button>
                        </DrawerClose>
                      )
                    })}
                  </nav>
                </section>
              )
            })}
          </div>
        </DrawerContent>
      </nav>
    </Drawer>
  )
}
